'use client'

import 'swiper/css'

import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import { useMemo, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import ChevronRightIcon from '@/assets/icons/chevron-right.svg'
import {
  type PublicTaskBanner,
  resolveTaskBanner
} from '@/features/tasks/taskBanners'

import TaskBannerSlide from './TaskBannerSlide'
import S from './TasksHeroBanners.module.scss'

const SLIDE_DURATION_MS = 7000

type TasksHeroBannersProps = {
  banners: PublicTaskBanner[]
}

const TasksHeroBanners = ({ banners }: TasksHeroBannersProps) => {
  const t = useTranslations('tasksPage')
  const resolved = useMemo(
    () => banners.map(banner => resolveTaskBanner(banner)),
    [banners]
  )
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLElement>(null)
  const isCarousel = resolved.length > 1

  return (
    <section
      ref={rootRef}
      className={S.root}
      aria-roledescription="carousel"
      aria-label={t('heroBanners.label')}
    >
      <h1 className={S.pageTitle}>{t('hero.headline')}</h1>
      <Swiper
        className={S.swiper}
        modules={[Autoplay]}
        slidesPerView={1}
        rewind={isCarousel}
        allowTouchMove={isCarousel}
        autoplay={
          isCarousel && {
            delay: SLIDE_DURATION_MS,
            pauseOnMouseEnter: true,
            disableOnInteraction: false
          }
        }
        onSwiper={setSwiper}
        onSlideChange={s => setActive(s.realIndex)}
        // Whole device pixels only: at a fractional offset the edge of the image
        // shows as a light seam while a slide is held mid-drag.
        onSetTranslate={(s, translate) => {
          const dpr = window.devicePixelRatio || 1
          const snapped = Math.round(translate * dpr) / dpr
          if (snapped !== translate) {
            s.wrapperEl.style.transform = `translate3d(${snapped}px, 0px, 0px)`
          }
        }}
        // Written straight to a CSS variable: a state update per frame would re-render every slide.
        onAutoplayTimeLeft={(_, __, left) =>
          rootRef.current?.style.setProperty(
            '--banner-progress',
            String(1 - left)
          )
        }
      >
        {resolved.map((banner, index) => (
          <SwiperSlide key={banner.id}>
            <TaskBannerSlide banner={banner} eager={index === 0} />
          </SwiperSlide>
        ))}
      </Swiper>

      {isCarousel && (
        <div className={S.controls}>
          <div className={S.bars}>
            {resolved.map((banner, index) => (
              <button
                key={banner.id}
                type="button"
                className={clsx(S.bar, index === active && S.barActive)}
                aria-label={t('heroBanners.goTo', { index: index + 1 })}
                aria-current={index === active}
                onClick={() => swiper?.slideTo(index)}
              />
            ))}
          </div>
          <div className={S.arrows}>
            <button
              type="button"
              className={clsx(S.arrow, S.arrowPrev)}
              aria-label={t('heroBanners.previous')}
              onClick={() => swiper?.slidePrev()}
            >
              <ChevronRightIcon aria-hidden />
            </button>
            <button
              type="button"
              className={S.arrow}
              aria-label={t('heroBanners.next')}
              onClick={() => swiper?.slideNext()}
            >
              <ChevronRightIcon aria-hidden />
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

export default TasksHeroBanners
